import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import UnfoldLessRoundedIcon from "@mui/icons-material/UnfoldLessRounded";
import {showSnackbar} from "./Snackbar";
import {elapsedCallSeconds, formatCallTime} from "./callTime";

const queueLabel = (entries) => {
  const first = entries[0]?.queueNumber;
  const last = entries[entries.length - 1]?.queueNumber;
  return first === last ? `Queue ${first}` : `Queue ${first}-${last}`;
};

const copyCallerId = async (entry) => {
  if (String(entry.id).includes("null")) {
    showSnackbar("Anonymous caller.", "warning");
    return;
  }
  try {
    await navigator.clipboard.writeText(entry.id);
    showSnackbar("Caller ID copied.");
  } catch (_) {
    showSnackbar("Caller ID could not be copied.", "warning");
  }
};

const MemberCard = ({entry, now, reviewerMonitoring}) => {
  const monitor = reviewerMonitoring?.getState(entry) || {};
  return (
  <Paper
    elevation={0}
    sx={{
      flex: "0 0 auto",
      width: {xs: "100%", sm: 142, md: 150, xl: 158},
      height: 212,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "9px",
      bgcolor: "background.paper",
    }}
  >
    <Stack alignItems="center" sx={{height: "100%", px: 1.1, py: 1}}>
      <Stack direction="row" alignItems="center" sx={{width: "100%"}}>
        <Chip size="small" label={entry.queueNumber} sx={{fontWeight: 700}} />
        <Box sx={{flex: 1}} />
        {monitor.available || monitor.listening ? <Tooltip
          title={monitor.listening ? "Stop listening" : "Listen to this call"}
        >
          <span>
            <IconButton
              size="small"
              color={monitor.listening ? "primary" : "default"}
              disabled={monitor.joining}
              onClick={() => reviewerMonitoring.listen(entry)}
              aria-label={monitor.listening ?
                `Stop listening to ${entry.answeredBy}` :
                `Listen to ${entry.answeredBy}'s call`}
            >
              <HeadphonesRoundedIcon sx={{fontSize: 18}} />
            </IconButton>
          </span>
        </Tooltip> : <Tooltip title="Call is being handled">
          <PhoneInTalkRoundedIcon color="success" sx={{fontSize: 17}} />
        </Tooltip>}
      </Stack>

      <Tooltip title={String(entry.id).includes("null") ? "Anonymous caller" : "Copy caller ID"}>
        <span>
          <IconButton
            disabled={String(entry.id).includes("null")}
            onClick={() => copyCallerId(entry)}
            aria-label={`Copy caller ID for queue ${entry.queueNumber}`}
            sx={{mt: 2.1, width: 52, height: 52, borderRadius: "12px", bgcolor: "action.hover"}}
          >
            <PersonRoundedIcon sx={{fontSize: 32, color: "text.primary"}} />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={entry.answeredBy || "Agent"}>
        <Typography noWrap sx={{mt: 1.2, width: "100%", textAlign: "center", fontSize: "0.82rem", fontWeight: 680}}>
          {entry.answeredBy || "Agent"}
        </Typography>
      </Tooltip>
      <Typography
        variant="caption"
        color="success.main"
        sx={{mt: 0.3, fontWeight: 650, fontVariantNumeric: "tabular-nums"}}
      >
        {entry.timeAnswered ?
          `Live · ${formatCallTime(elapsedCallSeconds(entry.timeAnswered, now))}` :
          "On a call"}
      </Typography>
      <Tooltip title={String(entry.id).includes("null") ? "Anonymous caller" : entry.id}>
        <Typography variant="caption" color="text.secondary" noWrap sx={{mt: "auto", width: "100%", textAlign: "center"}}>
          {String(entry.id).includes("null") ? "Anonymous" : entry.id}
        </Typography>
      </Tooltip>
    </Stack>
  </Paper>
  );
};

export default function ExpandedActiveCallGroup({
  entries,
  now,
  onCollapse,
  reviewerMonitoring,
}) {
  const count = entries.length;

  return (
    <Paper
      component="section"
      aria-label={`${count} calls handled by other agents, expanded`}
      elevation={0}
      sx={{
        flex: "0 0 auto",
        alignSelf: "flex-start",
        width: {xs: "100%", sm: "max-content"},
        maxWidth: {xs: "100%", sm: "none"},
        height: {xs: "auto", sm: 300},
        minHeight: {xs: 0, sm: 300},
        boxSizing: "border-box",
        p: 0.9,
        border: "1px solid",
        borderLeft: "4px solid",
        borderColor: "divider",
        borderLeftColor: "primary.main",
        borderRadius: "10px",
        bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(25,48,42,0.72)" : "rgba(232,244,239,0.78)",
        scrollSnapAlign: "start",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        spacing={0.8}
        sx={{
          minHeight: 34,
          mb: 0.8,
          px: 0.4,
          position: {xs: "static", sm: "sticky"},
          left: 0,
          width: "max-content",
          maxWidth: {xs: "100%", sm: "calc(100vw - 132px)"},
          zIndex: 2,
        }}
      >
        <GroupsRoundedIcon color="primary" sx={{fontSize: 20}} />
        <Typography variant="subtitle2" sx={{fontWeight: 700}}>
          {count} {count === 1 ? "call" : "calls"} handled by other agents
        </Typography>
        <Chip size="small" label={queueLabel(entries)} variant="outlined" />
        <Button
          size="small"
          startIcon={<UnfoldLessRoundedIcon />}
          onClick={onCollapse}
          sx={{ml: 0.5, whiteSpace: "nowrap"}}
        >
          Collapse calls
        </Button>
      </Stack>

      <Stack direction={{xs: "column", sm: "row"}} spacing={0.75}>
        {entries.map((entry) => (
          <MemberCard
            key={entry.id}
            entry={entry}
            now={now}
            reviewerMonitoring={reviewerMonitoring}
          />
        ))}
      </Stack>
    </Paper>
  );
}
